import { useEffect, useState } from 'react';
import { InputGroup } from '../../input-group';
import { Switch } from '@/components/shadcn/switch';
import { UpsertGrantSettingsMutation } from './grants-tab';
import { useMutation } from '@apollo/client';
import { useOrganizationContext } from '@/contexts/organization-context';
import { useToast } from '@/components/shadcn/use-toast';
import { logError } from '@/lib/rum';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { hasAdminRole } from '@/lib/auth-service';
import { handleOnlyNumber } from '@/lib/utils';

type GrantWritingSettingsProps = {
  grantWriting?: boolean;
  grantWritingCredit?: number;
  setLoading: any;
};

export default function GrantWritingSettings({
  grantWriting,
  grantWritingCredit,
  setLoading,
}: GrantWritingSettingsProps) {
  const { toast } = useToast();
  const session = useSessionContext();
  const { organizationId } = useOrganizationContext();

  const [grantWritingCredits, setGrantWritingCredits] = useState(0);

  useEffect(() => {
    setGrantWritingCredits(grantWritingCredit ?? 0);
  }, [grantWritingCredit]);

  const [upsertGrantSettings] = useMutation(UpsertGrantSettingsMutation, {
    refetchQueries: ['FetchGrantSettings'],
    onCompleted: () => {
      toast({
        description: 'Grants writing settings updated successfully',
      });
      setLoading(false);
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        description: 'Failed to grant writing settings',
      });
      logError('Failed to update grant writing settings', error);
      setLoading(false);
    },
  });

  if (session.loading || !session.doesSessionExist) {
    return null;
  }

  if (!hasAdminRole(session.accessTokenPayload['st-role']?.v)) {
    return null;
  }

  return (
    <div>
      <div className='mb-3 flex items-center space-x-2'>
        <label htmlFor='airplane-mode'>
          <span
            className={`${grantWriting ? 'text-destructive' : 'text-primary'}`}
          >
            {grantWriting ? 'Disable' : 'Enable'}
          </span>{' '}
          Grant Writing
        </label>
        <Switch
          id='grant-writing'
          checked={grantWriting ?? false}
          onClick={() => {
            setLoading(true);
            upsertGrantSettings({
              variables: {
                input: {
                  organizationId: organizationId as string,
                  grantWriting: !grantWriting,
                },
              },
            });
          }}
        />
      </div>
      {grantWriting && (
        <InputGroup
          labelTitle='Grant Writing Credit'
          placeholder='Grant Writing Credit'
          value={grantWritingCredits}
          onKeyDown={handleOnlyNumber}
          onChange={(e) => setGrantWritingCredits(Number(e.target.value))}
          min={0}
          max={99}
          onBlur={() => {
            if (grantWritingCredits === grantWritingCredit) return;

            setLoading(true);

            upsertGrantSettings({
              variables: {
                input: {
                  organizationId: organizationId as string,
                  grantWritingCredit: grantWritingCredits,
                },
              },
            });
          }}
        />
      )}
    </div>
  );
}
